import { fetchQuote } from "../constants/api";
import { getRouteDetails } from "../helpers/getRouteDetails";
import { formatUnits, parseUnits } from "viem";
import { getConfig } from "../constants/config";
import tokenService from "../../common/tokenService";

/**
 * Fetches the best swap route for a token pair using the Symphony API.
 * Handles both native and ERC20 tokens, with support for raw and formatted amounts.
 *
 * @param {string} tokenIn - Input token address ("0x0" for native SEI)
 * @param {string} tokenOut - Output token address
 * @param {string|number|bigint} amountIn - Input amount
 *   - If isRaw=true: Amount in raw units (wei)
 *   - If isRaw=false: Amount in formatted units (e.g., "1.0")
 * @param {Object} [options] - Route options
 * @param {boolean} [options.isRaw=false] - If true, amounts are in raw units
 *   Note: When amountIn is bigint, isRaw is automatically set to true
 *
 * @returns {Promise<Object>} Route details containing:
 *   - route {Array} - Raw route data from API
 *   - amountIn {bigint} - Input amount in raw units
 *   - amountInFormatted {string} - Input amount formatted
 *   - amountOut {bigint} - Output amount in raw units
 *   - amountOutFormatted {string} - Output amount formatted
 *   - pathPercentages {number[]} - Split between paths
 *   - pathCount {number} - Number of paths
 *   - includesNative {boolean} - Whether route includes native token
 *   - tokenIn {string} - Input token address
 *   - tokenOut {string} - Output token address
 *   - tokenInUsd {number} - Input token price in USD
 *   - tokenOutUsd {number} - Output token price in USD
 *   - priceImpactPercentage {number} - Price impact percentage
 *   - swapFee {string} - Swap fee
 *   Returns object with undefined values (except tokenIn/Out) if route fetch fails
 *
 * @throws {Error} If required parameters are missing
 * @throws {Error} If API request fails
 *
 * @example
 * // Example 1: Get route for SEI -> USDC swap
 * const route = await getRoute(
 *   "0x0", // SEI
 *   "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48", // USDC
 *   "1.0" // 1 SEI
 * );
 * console.log(route.amountOutFormatted); // Expected USDC amount
 *
 * @example
 * // Example 2: Get route with raw amount
 * const route = await getRoute(
 *   "0xe30fedd158a2e3b13e9badaeabafc5516e95e8c7", // WSEI
 *   "0xdAC17F958D2ee523a2206206994597C13D831ec7", // USDT
 *   1000000000000000000n, // 1 WSEI in wei
 *   { isRaw: true }
 * );
 *
 * @note When using bigint amounts, isRaw is automatically set to true
 */
export const getRoute = async (
  tokenIn,
  tokenOut,
  amountIn,
  options = { isRaw: false }
) => {
  try {
    // Validate inputs
    if (!tokenIn || !tokenOut || !amountIn) {
      throw new Error("Error getting route: Arguments missing!");
    }

    // Normalize addresses to lowercase for consistent lookups
    tokenIn = tokenIn.toLowerCase();
    tokenOut = tokenOut.toLowerCase();

    if (typeof amountIn === "bigint" && !options.isRaw) {
      console.warn(
        `Route has parameters 'isRaw:${options.isRaw}' but amountIn is BigInt. Type BigInt can only represent raw amounts. Option 'isRaw' set to 'true'`
      );
      options.isRaw = true;
    }

    // Get config for non-token data
    const config = getConfig();
    const {
      nativeAddress,
      wrappedNativeAddress,
      chainId,
      additionalTokens,
      overrideDefaultTokens,
    } = config;

    // Get updated token list with user's configuration applied
    const tokens = await tokenService.getAsyncTokenList(
      chainId,
      additionalTokens,
      overrideDefaultTokens,
      nativeAddress
    );

    const includesNative =
      tokenIn === nativeAddress || tokenOut === nativeAddress;
    if (
      tokenIn === tokenOut ||
      (tokenIn === wrappedNativeAddress && tokenOut === nativeAddress) ||
      (tokenIn === nativeAddress && tokenOut === wrappedNativeAddress)
    ) {
      console.error(
        "Error getting route: Cannot swap between same tokens or do wrap/unwrap!"
      );
      return {
        route: undefined,
        rebate: undefined,
        amountInFormatted: undefined,
        amountIn: amountIn,
        amountOut: undefined,
        amountOutFormatted: undefined,
        pathPercentages: undefined,
        pathCount: undefined,
        includesNative: undefined,
        tokenIn,
        tokenOut,
        tokenInUsd: undefined,
        tokenOutUsd: undefined,
        priceImpactPercentage: undefined,
        swapFee: undefined,
      };
    }

    if (options.isRaw)
      amountIn = formatUnits(amountIn, tokens[tokenIn].attributes.decimals);

    // Prepare API request parameters

    const routeUrl = "/route";
    const params = {
      tokenIn:
        tokenIn.toLowerCase() === nativeAddress ? "0x0" : tokenIn.toLowerCase(),
      tokenOut:
        tokenOut.toLowerCase() === nativeAddress
          ? "0x0"
          : tokenOut.toLowerCase(),
      amountIn: amountIn.toString(),
    };

    // Fetch route from the API
    const routeResult = await fetchQuote(routeUrl, params);
    if (!routeResult?.data) {
      throw new Error("Error getting route: No data received from API.");
    }
    const route = routeResult.data;
    const rebate = routeResult.rebate;
    const tokenInUsd = routeResult.tokenInUSD;
    const tokenOutUsd = routeResult.tokenOutUSD;
    const swapFee = routeResult.swapFee;
    const priceImpactPercentage = routeResult.priceImpactPercentage;

    // Calculate route details
    const routeDetails = await getRouteDetails(route);

    return {
      route,
      rebate,
      amountInFormatted: amountIn,
      amountIn: parseUnits(amountIn, tokens[tokenIn].attributes.decimals),
      amountOut: routeDetails.totalOut,
      amountOutFormatted: routeDetails.totalOutFormatted,
      pathPercentages: routeDetails.pathPercentages,
      pathCount: routeDetails.pathCount,
      includesNative: includesNative,
      tokenIn,
      tokenOut,
      tokenInUsd,
      tokenOutUsd,
      priceImpactPercentage,
      swapFee,
    };
  } catch (error) {
    // Log custom error message and return undefined
    console.error(
      "Cannot get quote:",
      error.message,
      "This might mean token addresses are wrong or token does not have an active pool or have not been indexed by Symphony yet."
    );
    return {
      route: undefined,
      rebate: undefined,
      amountInFormatted: undefined,
      amountIn: amountIn,
      amountOut: undefined,
      amountOutFormatted: undefined,
      pathPercentages: undefined,
      pathCount: undefined,
      includesNative: undefined,
      tokenIn,
      tokenOut,
      tokenInUsd: undefined,
      tokenOutUsd: undefined,
      priceImpactPercentage: undefined,
      swapFee: undefined,
    };
  }
};
